import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Calendar,
  CalendarIcon,
  Clock,
  Dumbbell,
  LogIn,
  LogOut,
  MapPin,
  Menu,
  Moon,
  Star,
  Sun,
  Trophy,
  Users,
  X,
} from 'lucide-react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import {
  collection,
  doc,
  documentId,
  getDoc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { auth, cloudFunctions, db, googleProvider } from './firebase';
import {
  getFriendlyAuthErrorMessage,
  isPasswordUser,
  registerWithEmailPassword,
  sendPasswordReset,
  signInWithVerifiedEmailPassword,
  upsertUserInFirestore,
} from './authHelpers';
import AdminPage from './AdminPage';
import ProfilePage from './ProfilePage';
import { defaultSiteContent, pickSiteContent } from './siteContent';
import {
  formatDateKeyInBudapest,
  formatDateTimeInBudapest,
  getBudapestDateAtTime,
  getBudapestTodayKey,
  getBudapestSunTimes,
  getBudapestTrainingDeadlineDate,
  getUpcomingBudapestSundays,
} from './dateUtils';
import './App.css';

const navItems = [
  { id: 'hero', label: 'Főoldal' },
  { id: 'training', label: 'Csapatedzés' },
  { id: 'about', label: 'Rólam' },
  { id: 'pricing', label: 'Árazás' },
  { id: 'register', label: 'Jelentkezés' },
];

const stats = [
  { icon: Trophy, value: '20+', label: 'Év tapasztalat' },
  { icon: Users, value: '200+', label: 'Edzett játékos' },
  { icon: Clock, value: '9 500+', label: 'Megtartott edzés' },
];

const details = [
  { id: 'when', icon: Calendar, title: 'Mikor?', desc: 'Minden vasárnap' },
  { id: 'time', icon: Clock, title: 'Időpont', desc: '09:00 – 11:00' },
  {
    id: 'location',
    icon: MapPin,
    title: 'Helyszín',
    desc: 'Omega Sportcentrum',
    href: 'https://maps.app.goo.gl/nkNtZ4RSG4uWquyq8',
  },
  { id: 'capacity', icon: Users, title: 'Létszám', desc: 'Max. 12 fő / alkalom' },
  { id: 'level', icon: Dumbbell, title: 'Szint', desc: 'Haladó+' },
  { id: 'price', icon: Star, title: 'Ár', desc: '3.000 Ft / alkalom' },
];

const DEFAULT_TRAINING_CAPACITY = 12;
const THEME_STORAGE_KEY = 'pngsquash-theme-preference';
const COOKIE_NOTICE_STORAGE_KEY = 'pngsquash-cookie-notice-accepted';
const PUBLIC_HOME_URL = '/?home=1';

const getAutomaticTheme = (date = new Date()) => {
  const { sunrise, sunset } = getBudapestSunTimes(date);
  if (!sunrise || !sunset) {
    return 'light';
  }
  return date >= sunrise && date < sunset ? 'light' : 'dark';
};

const getStoredThemePreference = () => {
  if (typeof window === 'undefined') {
    return 'auto';
  }

  try {
    const storedValue = window.localStorage.getItem(THEME_STORAGE_KEY);
    return ['auto', 'light', 'dark'].includes(storedValue) ? storedValue : 'auto';
  } catch (error) {
    return 'auto';
  }
};

const useThemeMode = () => {
  const [themePreference, setThemePreference] = useState(getStoredThemePreference);
  const [automaticTheme, setAutomaticTheme] = useState(() => getAutomaticTheme());
  const effectiveTheme = themePreference === 'auto' ? automaticTheme : themePreference;
  const initialThemeRef = useRef(effectiveTheme);
  const hasThemeToggleInteractionRef = useRef(false);

  useEffect(() => {
    const refreshAutomaticTheme = () => {
      setAutomaticTheme(getAutomaticTheme());
    };

    refreshAutomaticTheme();
    const intervalId = window.setInterval(refreshAutomaticTheme, 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.dataset.theme = effectiveTheme;
    document.documentElement.style.colorScheme = effectiveTheme;
  }, [effectiveTheme]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
    } catch (error) {
      // The automatic theme still works if local storage is unavailable.
    }
  }, [themePreference]);

  const toggleTheme = () => {
    setThemePreference((current) => {
      const initialTheme = initialThemeRef.current;
      const oppositeTheme = initialTheme === 'dark' ? 'light' : 'dark';

      if (!hasThemeToggleInteractionRef.current) {
        hasThemeToggleInteractionRef.current = true;
        return (current === 'auto' ? automaticTheme : current) === 'dark' ? 'light' : 'dark';
      }

      if (current === oppositeTheme) {
        return initialTheme;
      }

      if (current === initialTheme) {
        return 'auto';
      }

      return oppositeTheme;
    });
  };

  return {
    theme: effectiveTheme,
    themePreference,
    onToggleTheme: toggleTheme,
  };
};

const legalPages = {
  '/adatkezelesi-tajekoztato': {
    titleKey: 'privacyPolicyTitle',
    textKey: 'privacyPolicyText',
  },
  '/cookie-tajekoztato': {
    titleKey: 'cookiePolicyTitle',
    textKey: 'cookiePolicyText',
  },
  '/impresszum': {
    titleKey: 'imprintTitle',
    textKey: 'imprintText',
  },
  '/szolgaltatasi-feltetelek': {
    titleKey: 'termsTitle',
    textKey: 'termsText',
  },
};

const footerLinks = [
  { href: '/adatkezelesi-tajekoztato', label: 'Adatkezelési tájékoztató' },
  { href: '/cookie-tajekoztato', label: 'Cookie tájékoztató' },
  { href: '/impresszum', label: 'Impresszum' },
  { href: '/szolgaltatasi-feltetelek', label: 'Szolgáltatási feltételek' },
];

const getUpcomingSundays = () =>
  getUpcomingBudapestSundays(5).map((training) => ({
    ...training,
    timeLabel: '09:00',
  }));

const getTrainingTimeLabel = (training = {}) => {
  if (training.startTime) {
    return training.startTime;
  }
  if (training.trainingStartAt?.toDate) {
    return formatDateTimeInBudapest(training.trainingStartAt.toDate(), {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return '09:00';
};

const getTrainingStartDate = (trainingDateKey) => {
  return getBudapestDateAtTime(trainingDateKey, 9);
};

const getTrainingDeadlineDate = (trainingDateKey, hour) => {
  return getBudapestTrainingDeadlineDate(trainingDateKey, hour);
};

const canRegisterForTraining = (trainingDateKey) => {
  const deadline = getTrainingDeadlineDate(trainingDateKey, 21);
  return deadline ? new Date() < deadline : false;
};

const canCancelTraining = (trainingDateKey) => {
  const deadline = getTrainingDeadlineDate(trainingDateKey, 15);
  return deadline ? new Date() < deadline : false;
};

const formatTrainingDeadline = (trainingDateKey) => {
  const deadline = getTrainingDeadlineDate(trainingDateKey, 21);
  if (!deadline) {
    return '';
  }
  return formatDateTimeInBudapest(deadline, {
    month: 'short',
    day: 'numeric',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatFreeCancellationDeadline = (trainingDateKey) => {
  const deadline = getTrainingDeadlineDate(trainingDateKey, 15);
  if (!deadline) {
    return '';
  }
  return formatDateTimeInBudapest(deadline, {
    month: 'short',
    day: 'numeric',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizeImagePosition = (value) => (typeof value === 'string' && value.trim() ? value.trim() : '50% 50%');

const getBackgroundStyle = (url, position = '50% 50%') => {
  if (typeof url !== 'string') {
    return undefined;
  }

  const normalizedUrl = url.trim();
  if (!normalizedUrl) {
    return undefined;
  }

  const escapedUrl = normalizedUrl.replace(/"/g, '\\"');
  return {
    background: `url("${escapedUrl}") ${normalizeImagePosition(position)} / cover no-repeat`,
  };
};

const getImageUrls = (...values) => {
  const urls = values.flatMap((value) => (Array.isArray(value) ? value : [value]));
  return [...new Set(urls.map((url) => (typeof url === 'string' ? url.trim() : '')).filter(Boolean))];
};

const parseTrainingFlowSteps = (value) => {
  if (typeof value !== 'string') {
    return [];
  }

  const normalized = value.replace(/\r\n/g, '\n').trim();
  if (!normalized) {
    return [];
  }

  const numberedMatches = [...normalized.matchAll(/(?:^|\n)\s*(\d+)\.\s+([^\n]+)\n([\s\S]*?)(?=\n\s*\d+\.\s+[^\n]+\n|$)/g)];
  const blocks = numberedMatches.length > 0
    ? numberedMatches.map((match) => `${match[2]}\n${match[3]}`.trim())
    : normalized.split(/\n\s*\n/);

  return blocks
    .map((item) => {
      const lines = item
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const [title = '', ...descriptionLines] = lines;
      return {
        title: title.replace(/^\d+\.\s*/, ''),
        description: descriptionLines.join(' '),
      };
    })
    .filter((item) => item.title || item.description);
};

const parsePricingBand = (value) => {
  if (typeof value !== 'string') {
    return { label: 'Nincs megadva', price: '' };
  }

  const text = value.trim();
  if (!text) {
    return { label: 'Nincs megadva', price: '' };
  }

  const dashMatch = text.match(/^(.*\S)\s+[–-]\s+(\d[\d\s.]*\s*(?:Ft|HUF).*)$/i);
  if (dashMatch) {
    return {
      label: dashMatch[1].trim(),
      price: dashMatch[2].trim(),
    };
  }

  const [label, ...priceParts] = text.split(':');
  if (priceParts.length === 0) {
    return { label: text, price: '' };
  }

  return {
    label: label.trim(),
    price: priceParts.join(':').trim(),
  };
};

const getContentBlocks = (value, pageTitle = '') => {
  if (typeof value !== 'string') {
    return [];
  }

  const normalizedTitle = pageTitle.trim().toLowerCase();
  return value
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .filter((block, index) => {
      if (index !== 0 || !normalizedTitle) {
        return true;
      }
      return block.replace(/^##\s*/, '').trim().toLowerCase() !== normalizedTitle;
    });
};

const renderContentBlock = (block, index) => {
  if (block.startsWith('## ')) {
    return <h2 key={`heading-${index}`}>{block.replace(/^##\s*/, '')}</h2>;
  }

  const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
  if (lines.length > 0 && lines.every((line) => line.startsWith('- '))) {
    return (
      <ul key={`list-${index}`}>
        {lines.map((line) => (
          <li key={line}>{line.replace(/^-\s*/, '')}</li>
        ))}
      </ul>
    );
  }

  return <p key={`paragraph-${index}`}>{block}</p>;
};

function Footer({ siteContent, theme, themePreference, onToggleTheme }) {
  const contactEmail = typeof siteContent.contactEmail === 'string' ? siteContent.contactEmail.trim() : '';
  const contactPhone = typeof siteContent.contactPhone === 'string' ? siteContent.contactPhone.trim() : '';
  const phoneHref = contactPhone ? `tel:${contactPhone.replace(/[^\d+]/g, '')}` : '';
  const themeLabel = themePreference === 'auto' ? 'Automatikus' : theme === 'dark' ? 'Sötét' : 'Világos';

  return (
    <footer className="footer">
      <div className="footer-inner container">
        <div className="footer-copy">{siteContent.footerText}</div>

        {(contactEmail || contactPhone) && (
          <div className="footer-contact">
            {contactPhone && <a href={phoneHref}>{contactPhone}</a>}
            {contactEmail && <a href={`mailto:${contactEmail}`}>{contactEmail}</a>}
          </div>
        )}

        <nav className="footer-links" aria-label="Adminisztratív oldalak">
          {footerLinks.map((link) => (
            <a key={link.href} href={link.href}>
              {link.label}
            </a>
          ))}
        </nav>

        <button
          type="button"
          className="footer-theme-toggle"
          onClick={onToggleTheme}
          aria-label={`Téma váltása, jelenlegi állapot: ${themeLabel}`}
        >
          {(themePreference === 'auto' || theme === 'light') && (
            <Sun className="theme-icon theme-icon-sun" size={16} aria-hidden="true" />
          )}
          <span>{themeLabel}</span>
          {(themePreference === 'auto' || theme === 'dark') && (
            <Moon className="theme-icon theme-icon-moon" size={16} aria-hidden="true" />
          )}
        </button>
      </div>
    </footer>
  );
}

function MainSite({ theme, themePreference, onToggleTheme }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(() => {
    const requestedSection = window.location.hash.replace(/^#/, '');
    return navItems.some((item) => item.id === requestedSection) ? requestedSection : 'hero';
  });
  const [siteContent, setSiteContent] = useState(defaultSiteContent);
  const [siteContentLoaded, setSiteContentLoaded] = useState(false);
  const [cookieNoticeVisible, setCookieNoticeVisible] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return window.localStorage.getItem(COOKIE_NOTICE_STORAGE_KEY) !== 'true';
    } catch (error) {
      return true;
    }
  });

  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authNotice, setAuthNotice] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authPasswordConfirm, setAuthPasswordConfirm] = useState('');
  const [passwordManagedByGoogleNoticeOpen, setPasswordManagedByGoogleNoticeOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userRegistrationDisabledByAdmin, setUserRegistrationDisabledByAdmin] = useState(false);
  const [registrationBusyDate, setRegistrationBusyDate] = useState('');
  const [registrationNotice, setRegistrationNotice] = useState(null);
  const [registrationConfirmDate, setRegistrationConfirmDate] = useState('');
  const [registrationCounts, setRegistrationCounts] = useState({});
  const [waitlistCounts, setWaitlistCounts] = useState({});
  const [trainingCapacities, setTrainingCapacities] = useState({});
  const [trainingWeekStatuses, setTrainingWeekStatuses] = useState({});
  const [trainingWeekDetails, setTrainingWeekDetails] = useState({});
  const [deletedTrainingDates, setDeletedTrainingDates] = useState([]);
  const [alreadyRegisteredDates, setAlreadyRegisteredDates] = useState([]);
  const [alreadyWaitlistedDates, setAlreadyWaitlistedDates] = useState([]);
  const [cancelBusyDate, setCancelBusyDate] = useState('');
  const [cancelConfirmDate, setCancelConfirmDate] = useState('');

  useEffect(() => {
    const requestedSection = window.location.hash.replace(/^#/, '');
    if (!siteContentLoaded || !navItems.some((item) => item.id === requestedSection)) {
      return;
    }

    const targetSection = requestedSection === 'register'
      ? document.getElementById('training-registration-options')
      : document.getElementById(requestedSection);
    targetSection?.scrollIntoView?.({ behavior: 'auto', block: 'start' });
    setActiveSection(requestedSection);
  }, [siteContentLoaded]);

  const upcomingSundays = useMemo(getUpcomingSundays, []);
  const trainingOptions = useMemo(() => {
    const todayKey = getBudapestTodayKey();
    const deletedDates = new Set(deletedTrainingDates);
    const byDate = new Map(
      upcomingSundays
        .filter((training) => !deletedDates.has(training.key))
        .map((training) => [training.key, training]),
    );

    Object.values(trainingWeekDetails).forEach((training) => {
      if (training.key >= todayKey && !deletedDates.has(training.key)) {
        byDate.set(training.key, {
          ...training,
          label: training.label || formatDateKeyInBudapest(training.key, {
            month: 'short',
            day: 'numeric',
            weekday: 'long',
          }),
          timeLabel: getTrainingTimeLabel(training),
        });
      }
    });

    return Array.from(byDate.values()).sort((left, right) => {
      const dateDiff = left.key.localeCompare(right.key);
      if (dateDiff !== 0) {
        return dateDiff;
      }
      return (left.timeLabel || '').localeCompare(right.timeLabel || '');
    });
  }, [deletedTrainingDates, trainingWeekDetails, upcomingSundays]);
  const sundayLabelByKey = useMemo(
    () => Object.fromEntries(trainingOptions.map((sunday) => [sunday.key, sunday.label])),
    [trainingOptions],
  );

  const showRegistrationNotice = (trainingDate, message) => {
    setRegistrationNotice({ trainingDate, message });
  };

  useEffect(() => {
    if (!registrationNotice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setRegistrationNotice((currentNotice) =>
        currentNotice === registrationNotice ? null : currentNotice,
      );
    }, 1800);

    return () => window.clearTimeout(timeoutId);
  }, [registrationNotice]);
  const registrationRules = useMemo(() => {
    if (typeof siteContent.registerRules !== 'string') {
      return [];
    }
    return siteContent.registerRules
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 5);
  }, [siteContent.registerRules]);
  const trainingFlowSteps = useMemo(() => {
    return parseTrainingFlowSteps(siteContent.trainingFlowSteps);
  }, [siteContent.trainingFlowSteps]);
  const pricingBands = useMemo(
    () => [siteContent.pricingBand1, siteContent.pricingBand2, siteContent.pricingBand3, siteContent.pricingBand4],
    [siteContent.pricingBand1, siteContent.pricingBand2, siteContent.pricingBand3, siteContent.pricingBand4],
  );
  const heroBackgroundUrls = useMemo(
    () => getImageUrls(siteContent.heroBackgroundUrls, siteContent.heroBackgroundUrl),
    [siteContent.heroBackgroundUrls, siteContent.heroBackgroundUrl],
  );
  const aboutBackgroundStyle = useMemo(
    () => getBackgroundStyle(siteContent.aboutBackgroundUrl, siteContent.aboutBackgroundPosition),
    [siteContent.aboutBackgroundPosition, siteContent.aboutBackgroundUrl],
  );
  const trainingBackgroundStyle = useMemo(
    () => getBackgroundStyle(siteContent.trainingBackgroundUrl, siteContent.trainingBackgroundPosition),
    [siteContent.trainingBackgroundPosition, siteContent.trainingBackgroundUrl],
  );
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const offset = window.scrollY + 120;
      const newActive = navItems.reduce((current, item) => {
        const section = document.getElementById(item.id);
        if (section && section.offsetTop <= offset) {
          return item.id;
        }
        return current;
      }, 'hero');
      setActiveSection(newActive);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setActiveHeroSlide(0);
  }, [heroBackgroundUrls.length]);

  useEffect(() => {
    if (heroBackgroundUrls.length < 2) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveHeroSlide((current) => (current + 1) % heroBackgroundUrls.length);
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [heroBackgroundUrls.length]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && isPasswordUser(user) && !user.emailVerified) {
        setCurrentUser(null);
        setAuthNotice('A belépéshez előbb erősítsd meg az e-mail címed.');
        void signOut(auth);
        return;
      }

      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setUserRegistrationDisabledByAdmin(false);
      return undefined;
    }

    const userRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        setUserRegistrationDisabledByAdmin(
          data.training_registration_disabled_by_admin === true
          || data.trainingRegistrationDisabledByAdmin === true,
        );
      },
      () => {
        setUserRegistrationDisabledByAdmin(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    const contentRef = doc(db, 'site_content', 'main');
    const unsubscribe = onSnapshot(
      contentRef,
      (snapshot) => {
        const remoteContent = snapshot.exists() ? snapshot.data() : {};
        setSiteContent({
          ...defaultSiteContent,
          ...pickSiteContent(remoteContent),
        });
        setSiteContentLoaded(true);
      },
      (error) => {
        console.warn('site_content betöltési hiba, alapértelmezett szövegek maradnak:', error);
        setSiteContentLoaded(true);
      },
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (authOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [authOpen]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'deleted_training_weeks'), (snapshot) => {
      const dates = [];
      snapshot.forEach((deletedDoc) => {
        dates.push(deletedDoc.id);
      });
      setDeletedTrainingDates(dates);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'training_weeks'), (snapshot) => {
      const counts = {};
      const waitlistCountsByDate = {};
      const capacities = {};
      const statuses = {};
      const details = {};
      snapshot.forEach((weekDoc) => {
        const data = weekDoc.data();
        const key = data.training_date || weekDoc.id;
        counts[key] = typeof data.count === 'number' ? data.count : 0;
        waitlistCountsByDate[key] = typeof data.waitlist_count === 'number' ? data.waitlist_count : 0;
        capacities[key] = typeof data.capacity === 'number' && data.capacity > 0 ? data.capacity : DEFAULT_TRAINING_CAPACITY;
        statuses[key] = {
          cancelledByAdmin: data.cancelled_by_admin === true,
        };
        details[key] = {
          key,
          label: formatDateKeyInBudapest(key, {
            month: 'short',
            day: 'numeric',
            weekday: 'long',
          }),
          timeLabel: getTrainingTimeLabel({
            startTime: typeof data.start_time === 'string' ? data.start_time : '',
            trainingStartAt: data.training_start_at,
          }),
          startTime: typeof data.start_time === 'string' ? data.start_time : '',
          trainingStartAt: data.training_start_at,
        };
      });
      setRegistrationCounts(counts);
      setWaitlistCounts(waitlistCountsByDate);
      setTrainingCapacities(capacities);
      setTrainingWeekStatuses(statuses);
      setTrainingWeekDetails(details);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setAlreadyRegisteredDates([]);
      setAlreadyWaitlistedDates([]);
      return undefined;
    }

    const dateKeys = trainingOptions.map((item) => item.key);
    if (dateKeys.length === 0) {
      setAlreadyRegisteredDates([]);
      setAlreadyWaitlistedDates([]);
      return undefined;
    }

    const registrationIds = dateKeys.map((trainingDate) => `${currentUser.uid}_${trainingDate}`);
    const registrationsQuery = query(
      collection(db, 'training_registrations'),
      where(documentId(), 'in', registrationIds),
    );
    const waitlistQuery = query(
      collection(db, 'training_waitlist'),
      where(documentId(), 'in', registrationIds),
    );

    const unsubscribeRegistrations = onSnapshot(registrationsQuery, (snapshot) => {
      const dates = [];
      snapshot.forEach((registrationDoc) => {
        const data = registrationDoc.data();
        if (typeof data.training_date === 'string') {
          dates.push(data.training_date);
        }
      });
      dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      setAlreadyRegisteredDates(dates);
    });
    const unsubscribeWaitlist = onSnapshot(waitlistQuery, (snapshot) => {
      const dates = [];
      snapshot.forEach((waitlistDoc) => {
        const data = waitlistDoc.data();
        if (typeof data.training_date === 'string') {
          dates.push(data.training_date);
        }
      });
      dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      setAlreadyWaitlistedDates(dates);
    });

    return () => {
      unsubscribeRegistrations();
      unsubscribeWaitlist();
    };
  }, [currentUser, trainingOptions]);

  const handleNavigate = (sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  const goToProfile = () => {
    setMobileOpen(false);
    window.location.href = '/profile';
  };

  const openAuth = (mode = 'login') => {
    setAuthMode(mode);
    setAuthNotice('');
    setAuthOpen(true);
    setMobileOpen(false);
  };

  const closeAuth = () => {
    setAuthOpen(false);
    setAuthNotice('');
  };

  const acceptCookieNotice = () => {
    try {
      window.localStorage.setItem(COOKIE_NOTICE_STORAGE_KEY, 'true');
    } catch (error) {
      // The notice can still be dismissed if local storage is unavailable.
    }
    setCookieNoticeVisible(false);
  };

  const handleGoogleAuth = async () => {
    setAuthBusy(true);
    setAuthNotice('');

    try {
      const result = await signInWithPopup(auth, googleProvider);
      await upsertUserInFirestore(db, result.user);

      setAuthNotice(
        authMode === 'register'
          ? 'Sikeres Google regisztráció, fiókod mentve a Firestore users kollekcióba.'
          : 'Sikeres Google bejelentkezés.',
      );
      setAuthOpen(false);
    } catch (error) {
      setAuthNotice(getFriendlyAuthErrorMessage(error));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleEmailAuth = async (event) => {
    event.preventDefault();
    setAuthBusy(true);
    setAuthNotice('');

    try {
      if (authMode === 'reset') {
        const resetResult = await sendPasswordReset({ cloudFunctions, email: authEmail });
        if (
          resetResult.status === 'google_provider'
          || resetResult.status === 'provider_without_password'
        ) {
          setPasswordManagedByGoogleNoticeOpen(true);
          return;
        }

        setAuthNotice('Ha ehhez az e-mail címhez tartozik fiók, elküldtük a jelszó-visszaállító levelet.');
        return;
      }

      if (authMode === 'register') {
        if (authPassword !== authPasswordConfirm) {
          setAuthNotice('A két jelszó nem egyezik.');
          return;
        }

        await registerWithEmailPassword({ auth, cloudFunctions, email: authEmail, password: authPassword });
        setAuthPassword('');
        setAuthPasswordConfirm('');
        setAuthNotice('Elküldtük a megerősítő e-mailt. A belépés csak a megerősítés után működik.');
        setAuthMode('login');
        return;
      }

      await signInWithVerifiedEmailPassword({
        auth,
        cloudFunctions,
        db,
        email: authEmail,
        password: authPassword,
      });
      setAuthOpen(false);
    } catch (error) {
      if (authMode === 'reset' && error?.code === 'auth/user-not-found') {
        setAuthNotice('Ha ehhez az e-mail címhez tartozik fiók, elküldtük a jelszó-visszaállító levelet.');
        return;
      }

      setAuthNotice(getFriendlyAuthErrorMessage(error));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setAuthNotice('');
      setAuthOpen(false);
      setRegistrationNotice(null);
    } catch (error) {
      setAuthNotice(`Kijelentkezési hiba: ${error?.message || 'ismeretlen hiba'}`);
    }
  };

  const requestTrainingRegistration = (trainingDate) => {
    if (!currentUser) {
      showRegistrationNotice(trainingDate, 'A jelentkezéshez előbb be kell jelentkezned.');
      return;
    }

    if (userRegistrationDisabledByAdmin) {
      showRegistrationNotice(trainingDate, 'Jelentkezés letiltva admin által.');
      return;
    }

    if (!canRegisterForTraining(trainingDate)) {
      showRegistrationNotice(trainingDate, `A jelentkezési határidő lejárt erre az alkalomra: ${sundayLabelByKey[trainingDate] || trainingDate}.`);
      return;
    }

    if (alreadyRegisteredDates.includes(trainingDate)) {
      showRegistrationNotice(trainingDate, 'Erre az alkalomra már korábban jelentkeztél.');
      return;
    }

    if (alreadyWaitlistedDates.includes(trainingDate)) {
      showRegistrationNotice(trainingDate, 'Erre az alkalomra már korábban feliratkoztál a várólistára.');
      return;
    }

    setRegistrationNotice(null);
    setRegistrationConfirmDate(trainingDate);
  };

  const handleTrainingRegistration = async (trainingDate) => {
    setRegistrationConfirmDate('');
    setRegistrationBusyDate(trainingDate);
    setRegistrationNotice(null);

    try {
      const userEmail = (currentUser.email || '').trim().toLowerCase();
      const userDisplayName = (currentUser.displayName || '').trim();
      const userSnapshot = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userSnapshot.exists() ? userSnapshot.data() : {};

      if (
        userData.training_registration_disabled_by_admin === true
        || userData.trainingRegistrationDisabledByAdmin === true
      ) {
        setUserRegistrationDisabledByAdmin(true);
        showRegistrationNotice(trainingDate, 'Jelentkezés letiltva admin által.');
        return;
      }

      const trainingDetails = trainingWeekDetails[trainingDate] || {};
      const getRegistrationStartAt = (training = {}) => {
        if (training.trainingStartAt?.toDate) {
          return training.trainingStartAt.toDate();
        }
        if (training.training_start_at?.toDate) {
          return training.training_start_at.toDate();
        }
        const startTime = typeof training.startTime === 'string'
          ? training.startTime
          : typeof training.start_time === 'string'
            ? training.start_time
            : '';
        if (startTime) {
          const [hour = 9, minute = 0] = startTime.split(':').map(Number);
          return getBudapestDateAtTime(trainingDate, hour || 0, minute || 0);
        }
        return getTrainingStartDate(trainingDate);
      };

      const currentCount = registrationCounts[trainingDate] || 0;
      const currentWaitlistCount = waitlistCounts[trainingDate] || 0;
      const currentCapacity = trainingCapacities[trainingDate] || DEFAULT_TRAINING_CAPACITY;
      const trainingStartAt = getRegistrationStartAt(trainingDetails);

      if (currentCount >= currentCapacity) {
        await setDoc(doc(db, 'training_waitlist', `${currentUser.uid}_${trainingDate}`), {
          user_id: currentUser.uid,
          user_email: userEmail,
          user_display_name: userDisplayName,
          training_date: trainingDate,
          training_start_at: trainingStartAt,
          waitlist_position: currentWaitlistCount + 1,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        });

        showRegistrationNotice(trainingDate, `Felkerültél a várólistára (${currentWaitlistCount + 1}. hely): ${sundayLabelByKey[trainingDate] || trainingDate}`);
        return;
      }

      await setDoc(doc(db, 'training_registrations', `${currentUser.uid}_${trainingDate}`), {
        user_id: currentUser.uid,
        user_email: userEmail,
        user_display_name: userDisplayName,
        training_date: trainingDate,
        training_start_at: trainingStartAt,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });

      showRegistrationNotice(trainingDate, `Sikeres jelentkezés: ${sundayLabelByKey[trainingDate] || trainingDate}`);
    } catch (error) {
      if (error?.code === 'permission-denied') {
        showRegistrationNotice(trainingDate, 'Sikertelen jelentkezés: nincs jogosultság a művelethez. Frissítsd az oldalt, majd próbáld újra.');
      } else {
        showRegistrationNotice(trainingDate, `Sikertelen jelentkezés: ${error?.message || 'ismeretlen hiba'}`);
      }
    } finally {
      setRegistrationBusyDate('');
    }
  };

  const requestCancelRegistration = (trainingDate) => {
    if (!currentUser) {
      showRegistrationNotice(trainingDate, 'A lemondáshoz be kell jelentkezned.');
      return;
    }

    setRegistrationNotice(null);
    setCancelConfirmDate(trainingDate);
  };

  const handleCancelRegistration = async (trainingDate) => {
    setCancelConfirmDate('');
    setCancelBusyDate(trainingDate);
    setRegistrationNotice(null);

    try {
      const userEmail = (currentUser.email || '').trim().toLowerCase();
      const userDisplayName = (currentUser.displayName || '').trim();
      const registrationRef = doc(db, 'training_registrations', `${currentUser.uid}_${trainingDate}`);
      const lateCancellationRef = doc(db, 'late_cancellations', `${currentUser.uid}_${trainingDate}`);

      const registrationSnap = await getDoc(registrationRef);
      if (!registrationSnap.exists()) {
        throw new Error('Nincs jelentkezés erre az alkalomra.');
      }
      const registrationData = registrationSnap.data();

      if (!canCancelTraining(trainingDate)) {
        const batch = writeBatch(db);
        const lateCancelledAt = serverTimestamp();
        batch.update(registrationRef, {
          late_cancelled: true,
          late_cancelled_at: lateCancelledAt,
          updatedAt: serverTimestamp(),
        });
        batch.set(
          lateCancellationRef,
          {
            user_id: currentUser.uid,
            user_email: userEmail || registrationData.user_email || '',
            user_display_name: userDisplayName || registrationData.user_display_name || '',
            training_date: trainingDate,
            training_start_at: registrationData.training_start_at || getTrainingStartDate(trainingDate),
            registration_id: registrationRef.id,
            fee_percent: 70,
            cancelled_at: lateCancelledAt,
            createdAt: registrationData.late_cancelled_at || lateCancelledAt,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        await batch.commit();
        showRegistrationNotice(trainingDate, 'Lemondás rögzítve. Az edzést megelőző nap 15:00 után a lemondás 70%-os fizetéssel jár.');
        return;
      }

      await runTransaction(db, async (transaction) => {
        const liveRegistrationSnap = await transaction.get(registrationRef);

        if (!liveRegistrationSnap.exists()) {
          throw new Error('Nincs jelentkezés erre az alkalomra.');
        }

        transaction.delete(registrationRef);
        transaction.delete(lateCancellationRef);
      });

      showRegistrationNotice(trainingDate, `Sikeres lemondás: ${sundayLabelByKey[trainingDate] || trainingDate}`);
    } catch (error) {
      if (error?.code === 'permission-denied') {
        showRegistrationNotice(trainingDate, 'Sikertelen lemondás: nincs jogosultság a művelethez (permission-denied).');
      } else {
        const codePart = error?.code ? ` (${error.code})` : '';
        showRegistrationNotice(trainingDate, `Sikertelen lemondás: ${error?.message || 'ismeretlen hiba'}${codePart}`);
      }
    } finally {
      setCancelBusyDate('');
    }
  };

  return (
    <div className="app-shell">
      <nav className="navbar">
        <div className="container navbar-inner">
          <button className="brand" onClick={() => handleNavigate('hero')}>
            PNG<span>SQUASH</span>
          </button>

          <div className="desktop-nav">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
              >
                {item.label}
              </button>
            ))}
            {currentUser ? (
              <div className="nav-user">
                <button type="button" className="nav-user-name-button" onClick={goToProfile}>
                  {currentUser.displayName || currentUser.email}
                </button>
                <button className="button-primary nav-login" onClick={handleSignOut}>
                  <LogOut size={16} />
                  Kilépés
                </button>
              </div>
            ) : (
              <button className="button-primary nav-login" onClick={() => openAuth('login')}>
                <LogIn size={16} />
                Belépés
              </button>
            )}
          </div>

          <button className="mobile-toggle" onClick={() => setMobileOpen((open) => !open)} aria-label="Menü">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileOpen && (
          <div className="mobile-menu">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                className={`mobile-nav-link ${activeSection === item.id ? 'active' : ''}`}
              >
                {item.label}
              </button>
            ))}
            {currentUser ? (
              <>
                <button className="mobile-nav-link mobile-login" onClick={goToProfile}>
                  Profil ({currentUser.displayName || currentUser.email})
                </button>
                <button className="mobile-nav-link" onClick={handleSignOut}>
                  Kijelentkezés
                </button>
              </>
            ) : (
              <button className="mobile-nav-link mobile-login" onClick={() => openAuth('login')}>
                Belépés / Regisztráció
              </button>
            )}
          </div>
        )}
      </nav>

      <main>
        <section id="hero" className="hero">
          {heroBackgroundUrls.length > 0 ? (
            heroBackgroundUrls.map((url, index) => (
              <div
                className={`hero-media hero-media-image hero-slide ${index === activeHeroSlide ? 'active' : ''}`}
                key={url}
                style={getBackgroundStyle(url, siteContent.heroBackgroundPositions?.[index])}
              />
            ))
          ) : (
            <div className="hero-media" />
          )}
          <div className={`hero-overlay ${heroBackgroundUrls.length > 0 ? 'hero-overlay-image' : ''}`} />
          <div className="container hero-content">
            <h1>
              {siteContent.heroTitleLine1}
              <br />
              <span className="text-glow">{siteContent.heroTitleHighlight}</span>
            </h1>
            <p>{siteContent.heroSubtitle}</p>
            <div className="hero-actions">
              <button onClick={() => handleNavigate('register')} className="button-primary">
                Jelentkezés
              </button>
              <button onClick={() => handleNavigate('training')} className="button-secondary">
                Részletek
              </button>
            </div>
          </div>
        </section>

        <section
          id="training"
          className={`section has-overlay ${trainingBackgroundStyle ? 'section-with-image' : 'parquet-bg'}`}
          style={trainingBackgroundStyle}
        >
          <div className={`section-overlay ${trainingBackgroundStyle ? 'section-overlay-image' : ''}`} />
          <div className="container section-content">
            <div className="section-heading">
              <h2 className="section-title">{siteContent.trainingTitle}</h2>
              <p className="section-subtitle">{siteContent.trainingSubtitle}</p>
            </div>

            <div className="detail-grid">
              {details.map((detail) => (
                <div key={detail.title} className="detail-card">
                  <detail.icon size={24} className="detail-icon" />
                  <div>
                    <div className="detail-title">{detail.title}</div>
                    {detail.href ? (
                      <a className="detail-desc detail-link" href={detail.href} target="_blank" rel="noreferrer">
                        {detail.desc}
                      </a>
                    ) : (
                      <div className="detail-desc">{detail.id === 'price' ? siteContent.trainingPrice : detail.desc}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="card card-large plan-card">
              <h3>{siteContent.trainingPlanTitle}</h3>
              {trainingFlowSteps.length > 0 ? (
                <ol>
                  {trainingFlowSteps.map((step, index) => (
                    <li key={`${index + 1}-${step.title}`}>
                      <span>{index + 1}</span>
                      <div>
                        <strong className="plan-step-title">{step.title}</strong>
                        {step.description && <p>{step.description}</p>}
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="plan-empty">Még nincs megadott edzésmenet.</p>
              )}
            </div>

          </div>
        </section>

        <section
          id="about"
          className={`section has-overlay ${aboutBackgroundStyle ? 'section-with-image' : 'parquet-bg'}`}
          style={aboutBackgroundStyle}
        >
          <div className={`section-overlay ${aboutBackgroundStyle ? 'section-overlay-image' : ''}`} />
          <div className="container section-content">
            <h2 className="section-title section-title-center">{siteContent.aboutTitle}</h2>

            <div className={`card card-large about-card ${siteContent.aboutProfileImageUrl ? 'has-photo' : ''}`}>
              {siteContent.aboutProfileImageUrl && (
                <figure className="about-portrait">
                  <img
                    src={siteContent.aboutProfileImageUrl}
                    alt="PNG Squash edző portré"
                    style={{ objectPosition: normalizeImagePosition(siteContent.aboutProfileImagePosition) }}
                  />
                </figure>
              )}
              <div className="about-copy">
                <p>{siteContent.aboutParagraph1}</p>
                <p>{siteContent.aboutParagraph2}</p>
              </div>
            </div>

            <div className="stats-grid">
              {stats.map((stat) => (
                <div key={stat.label} className="stat-item">
                  <stat.icon size={32} className="stat-icon" />
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="section section-gradient">
          <div className="container pricing-content">
            <div className="section-heading">
              <h2 className="section-title">{siteContent.pricingTitle}</h2>
              <p className="section-subtitle">{siteContent.pricingSubtitle}</p>
            </div>

            <div className="pricing-layout">
              <div className="pricing-capacity" aria-label="Résztvevők száma">
                <span>Maximum</span>
                <div className="pricing-capacity-line">
                  <strong>{DEFAULT_TRAINING_CAPACITY} fő</strong>
                  <small>/</small>
                  <strong>2 óra</strong>
                </div>
              </div>

              <div className="pricing-bands">
                {pricingBands.map((bandText, index) => {
                  const band = parsePricingBand(bandText);
                  return (
                    <div className="pricing-band" key={`pricing-band-${index + 1}`}>
                      <div className="pricing-band-copy">
                        <strong>{band.label}</strong>
                        {band.price && <span>{band.price}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section id="register" className="section section-gradient">
          <div className="container register-content">
            <div className="section-heading">
              <h2 className="section-title">{siteContent.registerTitle}</h2>
              <p className="section-subtitle">{siteContent.registerSubtitle}</p>
            </div>

            {registrationRules.length > 0 && (
              <div className="card card-large plan-card register-rules-card">
                <h3>Jelentkezési és lemondási szabályok</h3>
                <ol>
                  {registrationRules.map((rule, index) => (
                    <li key={`${index + 1}-${rule}`}>
                      <span>{index + 1}</span>
                      <div>
                        <p>{rule}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div id="training-registration-options" className="register-grid date-only">
              <div className="date-selector">
                <label className="date-selector-label">
                  <CalendarIcon size={16} />
                  Jelentkezés:
                </label>
                <div className="date-grid">
                  {trainingOptions.map((sunday) => {
                    const isAlreadyRegistered = alreadyRegisteredDates.includes(sunday.key);
                    const isAlreadyWaitlisted = alreadyWaitlistedDates.includes(sunday.key);
                    const count = registrationCounts[sunday.key] || 0;
                    const waitlistCount = waitlistCounts[sunday.key] || 0;
                    const capacity = trainingCapacities[sunday.key] || DEFAULT_TRAINING_CAPACITY;
                    const availableSpots = Math.max(capacity - count, 0);
                    const occupancyPercent = Math.min(Math.round((count / capacity) * 100), 100);
                    const isTrainingCancelled = trainingWeekStatuses[sunday.key]?.cancelledByAdmin === true;
                    const canRegister = !isTrainingCancelled && canRegisterForTraining(sunday.key);
                    const isRegistrationBlockedByAdmin = Boolean(currentUser && userRegistrationDisabledByAdmin);
                    const isRegistrationBusy = registrationBusyDate === sunday.key;
                    const isCancelBusy = cancelBusyDate === sunday.key;
                    const isFull = availableSpots === 0;
                    const canJoinWaitlist = canRegister && isFull && !isRegistrationBlockedByAdmin;
                    const cardRegistrationNotice = registrationNotice?.trainingDate === sunday.key
                      ? registrationNotice
                      : null;
                    const cardRegistrationNoticeStatus = cardRegistrationNotice
                      && (
                        cardRegistrationNotice.message.startsWith('Sikeres')
                        || cardRegistrationNotice.message.startsWith('Felkerültél')
                        || cardRegistrationNotice.message.startsWith('Lemondás rögzítve')
                      )
                      ? 'ok'
                      : 'error';

                    if (isAlreadyRegistered) {
                      return (
                        <div
                          key={sunday.key}
                          className={`date-card registered registered-card ${isTrainingCancelled ? 'cancelled' : ''}`}
                        >
                          <div className="date-card-registered-badge">Már jelentkeztél</div>
                          <div className="date-card-main">
                            <div>
                              <div className="date-card-label">{sunday.label}</div>
                              <div className="date-card-time">{sunday.timeLabel || '09:00'}</div>
                            </div>
                            <div className="date-card-spots">
                              <strong>{availableSpots}</strong>
                              <span>szabad hely</span>
                            </div>
                          </div>
                          <div className="date-card-meta">
                            <div className="date-card-count">
                              <Users size={14} />
                              <span>{count} / {capacity} fő</span>
                            </div>
                            <div className="date-card-deadline">
                              Díjmentes lemondási határidő: {formatFreeCancellationDeadline(sunday.key)}
                            </div>
                          </div>
                          <div className="date-card-progress" aria-label={`${occupancyPercent}% foglalt`}>
                            <div style={{ width: `${occupancyPercent}%` }} />
                          </div>
                          {cardRegistrationNotice && (
                            <div
                              className={`date-card-floating-notice ${cardRegistrationNoticeStatus}`}
                              role="status"
                              aria-live="polite"
                            >
                              {cardRegistrationNotice.message}
                            </div>
                          )}
                          <div className="date-card-cancel-wrap">
                            {isTrainingCancelled ? (
                              <button
                                type="button"
                                className="button-soft date-card-cancel-button date-card-cancelled-button"
                                disabled
                              >
                                Lemondva a szervező által
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="button-soft date-card-cancel-button"
                                onClick={() => requestCancelRegistration(sunday.key)}
                                disabled={isCancelBusy}
                              >
                                {isCancelBusy ? 'Lemondás...' : 'Lemondás'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    }

                    const registrationButtonDisabled =
                      !currentUser || !canRegister || isRegistrationBlockedByAdmin || isRegistrationBusy || isAlreadyWaitlisted;
                    const registrationButtonClass = currentUser && canJoinWaitlist
                      ? 'button-primary date-card-register-button waitlist'
                      : 'button-primary date-card-register-button';
                    const registrationButtonLabel = (() => {
                      if (isRegistrationBusy) {
                        return 'Folyamatban...';
                      }
                      if (isTrainingCancelled) {
                        return 'Lemondva a szervező által';
                      }
                      if (!currentUser) {
                        return 'Lépj be a fiókodba a jelentkezéshez';
                      }
                      if (isRegistrationBlockedByAdmin) {
                        return 'Jelentkezés letiltva admin által';
                      }
                      if (isAlreadyWaitlisted) {
                        return 'Már várólistán vagy';
                      }
                      if (canJoinWaitlist) {
                        return `Jelentkezés várólistára ${waitlistCount + 1}. helyre`;
                      }
                      return canRegister ? 'Jelentkezés' : 'Jelentkezés zárva';
                    })();

                    return (
                      <div
                        key={sunday.key}
                        className={`date-card ${!canRegister ? 'closed' : ''} ${isTrainingCancelled ? 'cancelled' : ''}`}
                      >
                        {!isTrainingCancelled && !canRegister && <div className="date-card-closed-badge">Jelentkezés zárva</div>}
                        <div className="date-card-main">
                          <div>
                            <div className="date-card-label">{sunday.label}</div>
                            <div className="date-card-time">{sunday.timeLabel || '09:00'}</div>
                          </div>
                          <div className="date-card-spots">
                            <strong>{availableSpots}</strong>
                            <span>szabad hely</span>
                          </div>
                        </div>
                        <div className="date-card-meta">
                          <div className="date-card-count">
                            <Users size={14} />
                            <span>{count} / {capacity} fő</span>
                          </div>
                          {waitlistCount > 0 && (
                            <div className="date-card-waitlist-count">{waitlistCount} fő várólistán</div>
                          )}
                          {canRegister && (
                            <div className="date-card-deadline">Jelentkezési határidő: {formatTrainingDeadline(sunday.key)}</div>
                          )}
                        </div>
                        <div className="date-card-actions">
                          <button
                            type="button"
                            className={registrationButtonClass}
                            onClick={() => requestTrainingRegistration(sunday.key)}
                            disabled={registrationButtonDisabled}
                          >
                            {registrationButtonLabel}
                          </button>
                        </div>
                        <div className="date-card-progress" aria-label={`${occupancyPercent}% foglalt`}>
                          <div style={{ width: `${occupancyPercent}%` }} />
                        </div>
                        {cardRegistrationNotice && (
                          <div
                            className={`date-card-floating-notice ${cardRegistrationNoticeStatus}`}
                            role="status"
                            aria-live="polite"
                          >
                            {cardRegistrationNotice.message}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer
        siteContent={siteContent}
        theme={theme}
        themePreference={themePreference}
        onToggleTheme={onToggleTheme}
      />

      {cookieNoticeVisible && (
        <div className="cookie-notice" role="region" aria-label="Cookie tájékoztató">
          <p>
            Az oldal a működéséhez szükséges technikai sütiket használ.
            {' '}
            <a href="/cookie-tajekoztato">Cookie tájékoztató</a>
          </p>
          <button type="button" className="button-primary" onClick={acceptCookieNotice}>
            Rendben
          </button>
        </div>
      )}

      {authOpen && (
        <div className="auth-overlay" onClick={closeAuth} role="dialog" aria-modal="true">
          <div className="auth-modal card" onClick={(event) => event.stopPropagation()}>
            <button className="auth-close" onClick={closeAuth} aria-label="Bezárás">
              <X size={18} />
            </button>

            <div className="auth-header">
              <div className="auth-brand">
                PNG<span>SQUASH</span>
              </div>
              <h2>
                {authMode === 'reset'
                  ? 'Jelszó-visszaállítás'
                  : authMode === 'login'
                    ? 'Bejelentkezés'
                    : 'Regisztráció'}
              </h2>
            </div>

            {authMode !== 'reset' && (
              <div className="auth-switch">
                <button
                  type="button"
                  className={authMode === 'login' ? 'active' : ''}
                  onClick={() => {
                    setAuthMode('login');
                    setAuthNotice('');
                  }}
                >
                  Belépés
                </button>
                <button
                  type="button"
                  className={authMode === 'register' ? 'active' : ''}
                  onClick={() => {
                    setAuthMode('register');
                    setAuthNotice('');
                  }}
                >
                  Regisztráció
                </button>
              </div>
            )}

            <div className="auth-google-panel">
              <p>
                {authMode === 'login'
                  ? 'Google fiókkal vagy e-mail címmel jelentkezhetsz be.'
                  : authMode === 'register'
                    ? 'Google fiókkal azonnal regisztrálhatsz, e-mailes regisztrációnál megerősítő linket küldünk.'
                    : 'Add meg az e-mail címed, és elküldjük a jelszó-visszaállító levelet.'}
              </p>

              {authMode !== 'reset' && (
                <button type="button" className="button-primary auth-submit" onClick={handleGoogleAuth} disabled={authBusy}>
                  <LogIn size={18} />
                  {authBusy
                    ? 'Folyamatban...'
                    : authMode === 'login'
                      ? 'Belépés Google-lel'
                      : 'Regisztráció Google-lel'}
                </button>
              )}
            </div>

            <form className="auth-form auth-email-form" onSubmit={handleEmailAuth}>
              <label className="auth-field">
                <span>E-mail cím</span>
                <input
                  className="auth-input"
                  type="email"
                  value={authEmail}
                  onChange={(event) => setAuthEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
              </label>

              {authMode !== 'reset' && (
                <label className="auth-field">
                  <span>Jelszó</span>
                  <input
                    className="auth-input"
                    type="password"
                    value={authPassword}
                    onChange={(event) => setAuthPassword(event.target.value)}
                    autoComplete={authMode === 'register' ? 'new-password' : 'current-password'}
                    required
                  />
                </label>
              )}

              {authMode === 'register' && (
                <>
                  <label className="auth-field">
                    <span>Jelszó még egyszer</span>
                    <input
                      className="auth-input"
                      type="password"
                      value={authPasswordConfirm}
                      onChange={(event) => setAuthPasswordConfirm(event.target.value)}
                      autoComplete="new-password"
                      required
                    />
                  </label>
                  <p className="auth-helper">Minimum 10 karakter, kisbetű, nagybetű és szám.</p>
                </>
              )}

              <button type="submit" className="button-primary auth-submit" disabled={authBusy}>
                <LogIn size={18} />
                {authBusy
                  ? 'Folyamatban...'
                  : authMode === 'reset'
                    ? 'Visszaállító e-mail küldése'
                    : authMode === 'register'
                      ? 'Regisztráció e-maillel'
                      : 'Belépés e-maillel'}
              </button>
            </form>

            {authNotice && <p className="auth-notice">{authNotice}</p>}

            <p className="auth-footnote">
              {authMode === 'reset' ? (
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode('login');
                    setAuthNotice('');
                  }}
                >
                  Vissza a belépéshez
                </button>
              ) : (
                <>
                  {authMode === 'login' ? 'Nincs még fiókod?' : 'Már van fiókod?'}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode(authMode === 'login' ? 'register' : 'login');
                      setAuthNotice('');
                    }}
                  >
                    {authMode === 'login' ? 'Regisztrálj!' : 'Jelentkezz be!'}
                  </button>
                  {authMode === 'login' && (
                    <>
                      {' '}·{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setAuthMode('reset');
                          setAuthNotice('');
                        }}
                      >
                        Elfelejtett jelszó
                      </button>
                    </>
                  )}
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {passwordManagedByGoogleNoticeOpen && (
        <div
          className="auth-overlay"
          onClick={() => setPasswordManagedByGoogleNoticeOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="google-password-notice-title"
        >
          <div className="late-cancel-modal card admin-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <button
              className="auth-close"
              onClick={() => setPasswordManagedByGoogleNoticeOpen(false)}
              aria-label="Bezárás"
            >
              <X size={18} />
            </button>
            <h2 id="google-password-notice-title">A jelszót a Google kezeli</h2>
            <p>
              Ehhez az e-mail címhez Google-belépés tartozik, ezért a jelszót nem ez az oldal kezeli.
              A jelszavadat a Google fiókodban tudod megváltoztatni.
            </p>
            <div className="admin-confirm-actions">
              <button
                type="button"
                className="button-primary"
                onClick={() => setPasswordManagedByGoogleNoticeOpen(false)}
              >
                Rendben
              </button>
            </div>
          </div>
        </div>
      )}

      {registrationConfirmDate && (
        <div
          className="auth-overlay"
          onClick={() => setRegistrationConfirmDate('')}
          role="dialog"
          aria-modal="true"
          aria-labelledby="registration-confirm-title"
        >
          <div className="late-cancel-modal card registration-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <button className="auth-close" onClick={() => setRegistrationConfirmDate('')} aria-label="Bezárás">
              <X size={18} />
            </button>
            <h2 id="registration-confirm-title">Biztosan jelentkezel?</h2>
            <p>Erre az edzésre készülsz jelentkezni:</p>
            <div className="registration-confirm-training-date">
              {sundayLabelByKey[registrationConfirmDate] || registrationConfirmDate}
            </div>
            <div className="registration-confirm-actions">
              <button type="button" className="button-soft" onClick={() => setRegistrationConfirmDate('')}>
                Mégsem
              </button>
              <button
                type="button"
                className="button-primary"
                onClick={() => handleTrainingRegistration(registrationConfirmDate)}
              >
                Jelentkezem
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelConfirmDate && (
        <div
          className="auth-overlay"
          onClick={() => setCancelConfirmDate('')}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-confirm-title"
        >
          <div className="late-cancel-modal card registration-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <button className="auth-close" onClick={() => setCancelConfirmDate('')} aria-label="Bezárás">
              <X size={18} />
            </button>
            <h2 id="cancel-confirm-title">
              {canCancelTraining(cancelConfirmDate) ? 'Biztosan lemondod?' : 'Késői lemondás'}
            </h2>
            <p>
              {canCancelTraining(cancelConfirmDate)
                ? 'Ezt az edzésjelentkezést készülsz lemondani:'
                : 'Ez már késői lemondásnak számít, ezért 70%-os fizetési kötelezettséggel jár:'}
            </p>
            <div className="registration-confirm-training-date">
              {sundayLabelByKey[cancelConfirmDate] || cancelConfirmDate}
            </div>
            <div className="registration-confirm-actions">
              <button type="button" className="button-soft" onClick={() => setCancelConfirmDate('')}>
                Mégsem
              </button>
              <button
                type="button"
                className="button-primary"
                onClick={() => handleCancelRegistration(cancelConfirmDate)}
              >
                Lemondom
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LegalPage({ page, theme, themePreference, onToggleTheme }) {
  const [siteContent, setSiteContent] = useState(defaultSiteContent);

  useEffect(() => {
    const contentRef = doc(db, 'site_content', 'main');
    const unsubscribe = onSnapshot(
      contentRef,
      (snapshot) => {
        const remoteContent = snapshot.exists() ? snapshot.data() : {};
        setSiteContent({
          ...defaultSiteContent,
          ...pickSiteContent(remoteContent),
        });
      },
      (error) => {
        console.warn('site_content betöltési hiba, alapértelmezett szövegek maradnak:', error);
      },
    );
    return () => unsubscribe();
  }, []);

  const title = siteContent[page.titleKey] || 'Adminisztráció';
  const blocks = getContentBlocks(siteContent[page.textKey], title);

  return (
    <div className="app-shell legal-shell">
      <nav className="legal-navbar">
        <div className="container navbar-inner">
          <button className="brand" onClick={() => (window.location.href = PUBLIC_HOME_URL)}>
            PNG<span>SQUASH</span>
          </button>
          <a className="button-soft legal-home-link" href={PUBLIC_HOME_URL}>
            Főoldal
          </a>
        </div>
      </nav>

      <main className="legal-main">
        <article className="container legal-document">
          <h1>{title}</h1>
          <div className="legal-document-body">
            {blocks.length > 0 ? (
              blocks.map(renderContentBlock)
            ) : (
              <p>Ez az oldal jelenleg szerkesztés alatt áll.</p>
            )}
          </div>
        </article>
      </main>

      <Footer
        siteContent={siteContent}
        theme={theme}
        themePreference={themePreference}
        onToggleTheme={onToggleTheme}
      />
    </div>
  );
}

function App() {
  const themeMode = useThemeMode();

  if (typeof window === 'undefined') {
    return <MainSite {...themeMode} />;
  }

  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  if (legalPages[normalizedPath]) {
    return <LegalPage page={legalPages[normalizedPath]} {...themeMode} />;
  }
  if (normalizedPath === '/admin') {
    return <AdminPage />;
  }
  if (normalizedPath === '/profile') {
    return <ProfilePage />;
  }

  return <HomeEntry {...themeMode} />;
}

function HomeEntry(themeMode) {
  const [initialUserChecked, setInitialUserChecked] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [publicHomeRequested] = useState(
    () => new URLSearchParams(window.location.search).get('home') === '1',
  );

  useEffect(() => {
    let initialStateHandled = false;

    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        if (initialStateHandled) {
          return;
        }
        initialStateHandled = true;

        const canOpenProfile = user && (!isPasswordUser(user) || user.emailVerified);
        if (publicHomeRequested) {
          window.history.replaceState({}, '', `/${window.location.hash}`);
        } else if (canOpenProfile) {
          window.history.replaceState({}, '', '/profile');
          setShowProfile(true);
        }
        setInitialUserChecked(true);
      },
      () => {
        if (!initialStateHandled) {
          initialStateHandled = true;
          setInitialUserChecked(true);
        }
      },
    );

    return () => unsubscribe();
  }, [publicHomeRequested]);

  if (!initialUserChecked) {
    return null;
  }

  if (showProfile) {
    return <ProfilePage />;
  }

  return <MainSite {...themeMode} />;
}

export default App;
